/**
 * Minimal fixed-point decimal arithmetic over the API's money strings.
 *
 * The repo's rule is "never use float for money", and it doesn't stop at the
 * Python boundary: `Number("0.1") + Number("0.2")` is 0.30000000000000004 in a
 * browser too. Every money value arrives as a Decimal-exact string, so the few
 * operations the UI genuinely needs (add, compare, round for display) are done
 * on scaled BigInts here and handed back as strings.
 *
 * Rounding is half-even, matching Python's default decimal context — so a
 * value formatted here reads the same as the one the desktop app shows.
 */

export interface Decimal {
  /** Unscaled value, sign included: value === units / 10n ** scale. */
  units: bigint;
  scale: number;
}

const ZERO: Decimal = { units: 0n, scale: 0 };

const NUMERIC = /^([+-]?)(\d*)(?:\.(\d*))?(?:[eE]([+-]?\d+))?$/;

/**
 * Parse a Decimal string as the API serialises it. Returns null for anything
 * unparseable so callers can show a placeholder instead of throwing mid-render.
 */
export function parseDecimal(text: string | null | undefined): Decimal | null {
  if (text === null || text === undefined) return null;
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const match = NUMERIC.exec(trimmed);
  if (!match) return null;

  const [, sign, whole = "", fraction = "", exponent] = match;
  if (whole === "" && fraction === "") return null;

  let units = BigInt((whole || "0") + fraction);
  let scale = fraction.length;
  if (sign === "-") units = -units;

  // Normalise scientific notation into the same units/scale representation.
  const exp = exponent ? Number(exponent) : 0;
  if (exp > 0) {
    if (exp >= scale) {
      units *= 10n ** BigInt(exp - scale);
      scale = 0;
    } else {
      scale -= exp;
    }
  } else if (exp < 0) {
    scale += -exp;
  }
  return { units, scale };
}

/** Parse, falling back to zero — for arithmetic where a blank means "nothing". */
export function parseOrZero(text: string | null | undefined): Decimal {
  return parseDecimal(text) ?? ZERO;
}

function align(a: Decimal, b: Decimal): [bigint, bigint, number] {
  const scale = Math.max(a.scale, b.scale);
  return [
    a.units * 10n ** BigInt(scale - a.scale),
    b.units * 10n ** BigInt(scale - b.scale),
    scale,
  ];
}

export function addDecimal(a: Decimal, b: Decimal): Decimal {
  const [left, right, scale] = align(a, b);
  return { units: left + right, scale };
}

export function isZero(value: Decimal): boolean {
  return value.units === 0n;
}

export function isNegative(value: Decimal): boolean {
  return value.units < 0n;
}

export function compareDecimal(a: Decimal, b: Decimal): number {
  const [left, right] = align(a, b);
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

/** Render without rounding, trailing zeros intact — the canonical form. */
export function decimalToString(value: Decimal): string {
  const negative = value.units < 0n;
  const digits = (negative ? -value.units : value.units).toString();
  if (value.scale === 0) return (negative ? "-" : "") + digits;
  const padded = digits.padStart(value.scale + 1, "0");
  const cut = padded.length - value.scale;
  return `${negative ? "-" : ""}${padded.slice(0, cut)}.${padded.slice(cut)}`;
}

/**
 * Round to `places` decimals, half-even (Python's default), and return the
 * digit string plus its sign — the pieces a formatter needs.
 *
 * The sign reported is the *input's* sign, not a re-derivation from the
 * rounded digits. A value that rounds to all-zero digits (e.g. -0.001 at 2
 * places) is still a negative number and must say so — Python's own
 * `format_money` does exactly this (`Decimal("-0.001")` formats as "-0.00"),
 * and silently dropping the sign here would misreport the value, not just
 * its rounding.
 */
function roundHalfEven(value: Decimal, places: number): { negative: boolean; digits: string } {
  const negative = value.units < 0n;
  let units = negative ? -value.units : value.units;

  if (value.scale > places) {
    const divisor = 10n ** BigInt(value.scale - places);
    const quotient = units / divisor;
    const remainder = units * 2n - quotient * divisor * 2n; // remainder * 2
    const boundary = divisor;
    if (remainder > boundary || (remainder === boundary && quotient % 2n === 1n)) {
      units = quotient + 1n;
    } else {
      units = quotient;
    }
  } else {
    units *= 10n ** BigInt(places - value.scale);
  }

  const digits = units.toString().padStart(places + 1, "0");
  return { negative, digits };
}

function withGrouping(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Money for display: fixed decimals, thousands separators.
 * Mirrors ``utilities.formatting.format_money`` — including "—" for null.
 */
export function formatMoney(text: string | null | undefined, places = 2): string {
  const value = parseDecimal(text);
  if (value === null) return "—";
  const { negative, digits } = roundHalfEven(value, places);
  const cut = digits.length - places;
  const whole = withGrouping(digits.slice(0, cut) || "0");
  const fraction = digits.slice(cut);
  return `${negative ? "-" : ""}${whole}${places > 0 ? `.${fraction}` : ""}`;
}

export function formatMoneyDecimal(value: Decimal, places = 2): string {
  return formatMoney(decimalToString(value), places);
}

/**
 * Prices and lots: full precision, trailing zeros stripped.
 * Mirrors ``utilities.formatting.format_price``.
 *
 * The sign is reported whenever the input was negative, even if every
 * displayed digit is zero (e.g. "-0.00000") — the value is negative, and
 * this must say so rather than round it away. Python's own format_price does
 * the same (Decimal("-0.00000") formats as "-0", not "0").
 */
export function formatPrice(text: string | null | undefined): string {
  const value = parseDecimal(text);
  if (value === null) return "—";
  const canonical = decimalToString(value);
  const negative = canonical.startsWith("-");
  const body = negative ? canonical.slice(1) : canonical;
  const [wholeRaw = "0", fraction = ""] = body.split(".");
  const trimmedFraction = fraction.replace(/0+$/, "");
  const whole = withGrouping(wholeRaw);
  const text2 = trimmedFraction ? `${whole}.${trimmedFraction}` : whole;
  return `${negative ? "-" : ""}${text2 || "0"}`;
}

/** `$1,234.56`, or a plain dash when there is no value at all. */
export function formatUsd(text: string | null | undefined, places = 2): string {
  const formatted = formatMoney(text, places);
  return formatted === "—" ? "—" : `$${formatted}`;
}

export function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}
